import React from 'react';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Select } from 'components/partials/select/select';
import Input from 'components/partials/input/input';
import IconButton from 'components/partials/button/icon-button';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';
import ExpanderCell from 'components/partials/table-component/expander-cell/expander-cell';

import {
  FUND_SOURCE_LABELS,
  FUND_TYPE_LABELS,
  isFundSourceLabelKey,
  isFundTypeLabelKey,
} from './funds-step.constants';

import Icons from 'assets/icons';

const commonHeaderStyles =
  'h-11 border-r-2 border-white font-semibold cursor-pointer text-xs py-0';

const controlStyles = {
  padding: '0',
  minHeight: '32px',
  height: '32px',
  fontSize: '14px',
};

interface FundsTableSettingsParams {
  onDelete: (key: string | number) => void;
  onAwardingTypeChange: (key: string | number, value: string) => void;
  onAwardAmountChange: (
    key: string | number,
    awardIndex: number,
    value: unknown
  ) => void;
  awardingTypeOptions: { value: string; label: string }[];
  readOnly?: boolean;
}

type CellRow = Record<string, unknown> & {
  isParent?: boolean;
  childCount?: number;
  name?: string;
  source?: string;
  type?: string;
  id?: string | number;
  _tempId?: string | number;
  parentId?: string | number;
  awardIndex?: number;
  awardingType?: string;
  previousYearAmount?: number;
  previousYearRecipients?: number;
  currentYearAmount?: number;
};

interface CellInfo {
  row: {
    original: CellRow;
    getCanExpand: () => boolean;
  };
  renderValue: () => unknown;
}

export const getFundsTableSettings = ({
  onDelete,
  onAwardingTypeChange,
  onAwardAmountChange,
  awardingTypeOptions,
  readOnly,
}: FundsTableSettingsParams): unknown[] =>
  [
    {
      id: 'name',
      accessorKey: 'name',
      header: () => <span>FUND NAME</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (row.isParent) {
          const childCount = (row.childCount as number) || 0;
          const name = info.renderValue();
          const canExpand = info.row.getCanExpand();

          return (
            <span className="flex items-center gap-2 font-semibold text-gray-700">
              <TruncatedCell value={name as never} />
              {childCount > 0 && (
                <span className="whitespace-nowrap">({childCount})</span>
              )}
              {canExpand && <ExpanderCell row={info.row as never} />}
            </span>
          );
        }

        return <span className="font-normal text-gray-500">{row.name}</span>;
      },
      headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
      className: 'text-left px-2.5 py-2 w-[26%] max-w-0',
      enableSorting: true,
      sortingFn: 'alphanumeric',
    },
    {
      id: 'source',
      accessorKey: 'source',
      header: () => <span>SOURCE</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (!row.isParent) return null;

        const source = row.source ?? '';

        return (
          <TruncatedCell
            value={
              isFundSourceLabelKey(source) ? FUND_SOURCE_LABELS[source] : source
            }
          />
        );
      },
      headerClassName: commonHeaderStyles,
      className: 'text-left w-[11%] px-2.5 py-2 max-w-0',
      enableSorting: true,
      sortingFn: 'alphanumeric',
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: () => <span>TYPE</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (!row.isParent) return null;

        const type = row.type ?? '';

        return (
          <TruncatedCell
            value={isFundTypeLabelKey(type) ? FUND_TYPE_LABELS[type] : type}
          />
        );
      },
      headerClassName: commonHeaderStyles,
      className: 'text-left w-[10%] px-2.5 py-2 max-w-0',
      enableSorting: true,
      sortingFn: 'alphanumeric',
    },
    {
      id: 'previousYearAmount',
      header: () => <span>CURRENT YEAR</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (row.isParent) {
          return <span className="text-gray-400 text-sm">-</span>;
        }

        return (
          <span className="flex items-center gap-1.5">
            <span>$</span>
            <span>{row.previousYearAmount?.toLocaleString()}</span>
          </span>
        );
      },
      headerClassName: commonHeaderStyles,
      className: 'w-[9%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'totalRecipients',
      header: () => <span>TOTAL RECIPIENTS</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (row.isParent) {
          return <span className="text-gray-400 text-sm">-</span>;
        }

        return row.previousYearRecipients;
      },
      headerClassName: commonHeaderStyles,
      className: 'w-[13%] px-2.5 py-2',
      enableSorting: false,
    },
    {
      id: 'currentYearAmount',
      header: () => <span>FUTURE YEAR</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;

        if (row.isParent) {
          return <span className="text-gray-400 text-sm">-</span>;
        }

        const fundKey = row.parentId;

        return (
          <Stack
            direction="row"
            sx={{ justifyContent: 'center', px: 0.25, py: 0.5 }}
          >
            <Input
              type="text"
              value={
                row.currentYearAmount != null
                  ? `$ ${Number(row.currentYearAmount).toLocaleString()}`
                  : ''
              }
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');

                if (fundKey == null) return;

                onAwardAmountChange(
                  fundKey,
                  row.awardIndex ?? 0,
                  raw ? Number.parseFloat(raw) : null
                );
              }}
              placeholder="$ 0"
              disabled={readOnly}
              inputCustomClass="h-8 !py-1 !px-2.5 text-sm"
              inputWrapperCustomClass="!m-0"
              aria-label={`2026 amount for ${row.name}`}
              data-testid={`fund-amount-input-${row.id}`}
            />
          </Stack>
        );
      },
      headerClassName: commonHeaderStyles,
      className: 'w-[11%] px-2.5',
      enableSorting: false,
    },
    {
      id: 'awardingType',
      accessorKey: 'awardingType',
      header: () => <span>AWARDING LOGIC</span>,
      cell: (info: CellInfo) => {
        const row = info.row.original;
        const isParent = row.isParent;
        const options = awardingTypeOptions || [];

        const selectedOption = options.find(
          (opt) => opt.value === row.awardingType
        );

        return (
          <Box sx={{ px: 0.25, py: 0.5 }}>
            <Select
              options={options}
              value={selectedOption}
              onChange={(option) => {
                if (!isParent) return;

                const fundKey = row.id || row._tempId;

                if (fundKey == null) return;

                if (option && !Array.isArray(option)) {
                  onAwardingTypeChange(fundKey, String(option.value));
                }
              }}
              selectClass="w-full"
              placeholder="Select..."
              isSearchable={false}
              disabled={!isParent || readOnly}
              disabledBgColor="#F2F2F2"
              extraBasicStyles={controlStyles}
              extraStyles={() => ({
                valueContainer: (provided: Record<string, unknown>) => ({
                  ...provided,
                  height: '30px',
                  padding: '0 10px',
                  display: 'flex',
                  alignItems: 'center',
                }),
                singleValue: (provided: Record<string, unknown>) => ({
                  ...provided,
                  margin: 0,
                  lineHeight: '20px',
                }),
                indicatorsContainer: (provided: Record<string, unknown>) => ({
                  ...provided,
                  height: '30px',
                  alignItems: 'center',
                }),
                dropdownIndicator: (provided: Record<string, unknown>) => ({
                  ...provided,
                  padding: '0 10px 0 6px',
                }),
                input: (provided: Record<string, unknown>) => ({
                  ...provided,
                  margin: 0,
                  padding: 0,
                }),
                menuPortal: (provided: Record<string, unknown>) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              })}
              menuPortalTarget={document.body}
              aria-label={`Awarding logic for ${row.name}`}
              data-testid={`fund-awarding-select-${row.id}`}
            />
          </Box>
        );
      },
      headerClassName: `${commonHeaderStyles}${
        readOnly ? ' rounded-tr-md rounded-br-md' : ''
      }`,
      className: 'w-[16%] px-2.5',
      enableSorting: false,
    },
    ...(!readOnly
      ? [
          {
            id: 'actions',
            header: () => null,
            cell: (info: CellInfo) => {
              const row = info.row.original;

              if (!row.isParent) return null;

              return (
                <IconButton
                  variant="reserve-text"
                  size="xs"
                  icon={<Icons.Delete />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const key = row.id || row._tempId;

                    if (key != null) onDelete(key);
                  }}
                  className="mx-auto text-navy-500 hover:!bg-navy-50 hover:!text-navy-700 focus-visible:!ring-navy-300"
                  aria-label={`Remove ${row.name}`}
                  data-testid={`fund-remove-button-${row.id || row._tempId}`}
                />
              );
            },
            headerClassName: 'rounded-tr-md rounded-br-md text-xs h-8 py-0',
            className: 'w-[4%] px-2',
            enableSorting: false,
          },
        ]
      : []),
  ] as unknown[];
